import {
  installationTaskImportApplyResponseSchema,
  type InstallationTaskImportMode,
  installationTaskImportPreviewResponseSchema,
  type SensorType,
  type WoodType,
} from '@si/types';
import { postForm } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface BaseParams {
  projectId: string;
  installationId: string;
  file: File;
  mapWidth: number;
  mapHeight: number;
  sensorType: SensorType;
}

interface ApplyParams extends BaseParams {
  mode: InstallationTaskImportMode;
  groupKeys: string[];
  sourceTags: string[];
  includeWithoutTag: boolean;
  excludedItemIds: string[];
  saveSourceTagAsLabel: boolean;
  woodType: WoodType | null;
  woodScrewLengthMm: number | null;
  woodInsulatedElectrode: boolean | null;
}

function buildImportFormData(file: File, importRequest: Record<string, unknown>) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('importRequest', JSON.stringify(importRequest));
  return formData;
}

export function useImportInstallationTasks() {
  const queryClient = useQueryClient();

  const previewMutation = useMutation({
    mutationFn: async (params: BaseParams) => {
      return postForm({
        url: `/projects/${params.projectId}/installations/${params.installationId}/task-import/preview`,
        body: buildImportFormData(params.file, {
          mapWidth: params.mapWidth,
          mapHeight: params.mapHeight,
          sensorType: params.sensorType,
        }),
        schema: installationTaskImportPreviewResponseSchema,
      });
    },
  });

  const applyMutation = useMutation({
    mutationFn: async (params: ApplyParams) => {
      return postForm({
        url: `/projects/${params.projectId}/installations/${params.installationId}/task-import/apply`,
        body: buildImportFormData(params.file, {
          mapWidth: params.mapWidth,
          mapHeight: params.mapHeight,
          mode: params.mode,
          groupKeys: params.groupKeys,
          sourceTags: params.sourceTags,
          includeWithoutTag: params.includeWithoutTag,
          excludedItemIds: params.excludedItemIds,
          saveSourceTagAsLabel: params.saveSourceTagAsLabel,
          sensorType: params.sensorType,
          woodType: params.woodType,
          woodScrewLengthMm: params.woodScrewLengthMm,
          woodInsulatedElectrode: params.woodInsulatedElectrode,
        }),
        schema: installationTaskImportApplyResponseSchema,
      });
    },
    onSuccess: (_, { projectId, installationId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.getInstallationTasks(projectId, installationId),
      });
    },
  });

  const previewInstallationTasksImport = useCallback(
    (params: BaseParams) => previewMutation.mutate(params),
    [previewMutation]
  );

  const applyInstallationTasksImport = useCallback(
    (params: ApplyParams) => applyMutation.mutate(params),
    [applyMutation]
  );

  return {
    previewInstallationTasksImport,
    applyInstallationTasksImport,
    preview: previewMutation.data?.preview,
    applyResult: applyMutation.data?.result,
    previewError: previewMutation.error,
    applyError: applyMutation.error,
    isPreviewPending: previewMutation.isPending,
    isApplyPending: applyMutation.isPending,
    isApplySuccess: applyMutation.isSuccess,
    resetPreview: previewMutation.reset,
    resetApply: applyMutation.reset,
  };
}
